import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowUpFromBracket,
  faFileExcel,
  faFileLines,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { deleteConnection, uploadFile } from '../services/api';
import { hasValidationIssues, normalizeValidationIssues } from '../utils/uploadValidation';
import UploadRequirementsPanel from '../components/UploadRequirementsPanel';
import AnalyzingProgress from '../components/AnalyzingProgress';
import UploadAnalysisScreen from '../components/UploadAnalysisScreen';
import UploadValidationErrors from '../components/UploadValidationErrors';

const TYPE_META = {
  csv: { label: 'CSV', icon: faFileLines, accept: '.csv', desc: 'a CSV file' },
  excel: { label: 'Excel', icon: faFileExcel, accept: '.xlsx,.xls', desc: 'an Excel workbook' },
};

const ANALYZING_STEPS = [
  'Reading workbook',
  'Validating worksheets',
  'Detecting data types',
  'Building semantic metadata',
  'Preparing AI analysis',
];

function isAllowedFile(type, file) {
  const ext = file.name.split('.').pop().toLowerCase();
  return type === 'csv' ? ext === 'csv' : ['xlsx', 'xls'].includes(ext);
}

export default function ConnectUploadPage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const meta = TYPE_META[type];

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [phase, setPhase] = useState('form'); // 'form' | 'analyzing' | 'analysis' | 'invalid'
  const [analyzingDone, setAnalyzingDone] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [uploadValidation, setUploadValidation] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeoutCooldown, setTimeoutCooldown] = useState(false);
  const fileRef = useRef();
  // Ref (not state) so a second submit in the same tick — before React has
  // re-rendered the disabled button — is still blocked synchronously. This
  // is what keeps a same-name upload from firing twice and racing the
  // backend's file handle (WinError 32).
  const submittingRef = useRef(false);
  const cooldownTimerRef = useRef(null);

  useEffect(() => () => {
    if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
  }, []);

  if (!meta) {
    navigate('/dashboard', { replace: true });
    return null;
  }

  const handleFile = (f) => {
    if (!isAllowedFile(type, f)) {
      toast.error(type === 'csv' ? 'Please upload a .csv file' : 'Please upload .xlsx or .xls');
      return;
    }
    setFile(f);
    if (!name) setName(f.name.replace(/\.[^.]+$/, ''));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  const chooseDifferentFile = () => {
    setUploadValidation(null);
    setFile(null);
    setPhase('form');
  };

  const handleStartChat = () => {
    navigate('/dashboard', { state: { newConnection: analysis } });
  };

  const handleBackFromAnalysis = async () => {
    // The upload already persisted a connection on the backend before this
    // screen showed. Backing out without starting a chat means the user
    // declined it, so remove it before navigating — otherwise the sidebar's
    // connection list can load before the delete lands and briefly (or
    // permanently, if never refetched) still shows the declined upload.
    const connId = analysis?.id;
    if (connId) {
      try {
        await deleteConnection(connId);
      } catch {
        // ignore — nothing to clean up if it's already gone
      }
    }
    navigate('/dashboard', { state: { openConnectModal: true } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current || timeoutCooldown) return;
    if (!name.trim()) return toast.error('Enter a connection name');
    if (!file) return toast.error('Select a file first');

    submittingRef.current = true;
    setIsSubmitting(true);
    setAnalyzingDone(false);
    setPhase('analyzing');
    try {
      const uploadResult = await uploadFile(file, name.trim());
      // A validation failure can come back as a normal 200 response that
      // still reports errors/warnings, not just as a thrown error.
      if (hasValidationIssues(uploadResult)) {
        setUploadValidation(normalizeValidationIssues(uploadResult));
        setPhase('invalid');
        return;
      }
      // The upload response may nest the connection under a `connection`
      // key (alongside sibling analysis fields like dataset_summary) or
      // return it flat — normalize either way so `conn.id` always resolves.
      const conn = uploadResult?.connection && typeof uploadResult.connection === 'object'
        ? { ...uploadResult, ...uploadResult.connection }
        : uploadResult;
      setAnalyzingDone(true);
      await new Promise(r => setTimeout(r, 500));
      setAnalysis(conn);
      setPhase('analysis');
    } catch (err) {
      if (hasValidationIssues(err.data)) {
        setUploadValidation(normalizeValidationIssues(err.data));
        setPhase('invalid');
      } else if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
        // The client gave up waiting, but the backend may still be holding
        // the file open and processing it. Firing another request right now
        // would race that in-flight one against the same filename, which is
        // exactly the WinError 32 collision this is meant to prevent — so
        // block resubmission of this same file for a cooldown instead of
        // auto-retrying, and require the user to explicitly try again.
        toast.error(
          'This is taking longer than expected. Your file may still be processing on the server — please wait a moment before trying again.',
          { duration: 7000 },
        );
        setPhase('form');
        setTimeoutCooldown(true);
        cooldownTimerRef.current = setTimeout(() => setTimeoutCooldown(false), 20000);
      } else {
        toast.error(err.message || 'Failed to upload dataset');
        setPhase('form');
      }
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // While a file is being analyzed or the analysis result is on screen, no
  // connection is ready to chat with yet. If the user navigates back with
  // the browser's back button instead of clicking "Start Chat", send them
  // to a fresh "new connection" modal rather than leaving them on a dead
  // upload screen with no way to upload or chat.
  useEffect(() => {
    if (phase !== 'analyzing' && phase !== 'analysis') return;
    window.history.pushState({ uploadPhase: phase }, '');
    const handlePopState = async () => {
      const connId = analysis?.id;
      if (connId) {
        try {
          await deleteConnection(connId);
        } catch {
          // ignore — nothing to clean up if it's already gone
        }
      }
      navigate('/dashboard', { replace: true, state: { openConnectModal: true } });
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [phase, navigate, analysis]);

  const isReady = name.trim().length > 0 && !!file;
  const submitDisabled = !isReady || isSubmitting || timeoutCooldown;

  return (
    <div style={s.page}>
      <div style={s.container}>
        {phase === 'form' && (
          <>
            <Link to="/dashboard" style={s.backLink}>
              <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" /> Back to Dashboard
            </Link>
            <div style={s.header}>
              <div style={s.headerIcon}>
                <FontAwesomeIcon icon={meta.icon} aria-hidden="true" />
              </div>
              <div>
                <h1 style={s.title}>Connect {meta.label}</h1>
                <p style={s.subtitle}>
                  Upload {meta.desc === 'a CSV file' ? 'a CSV dataset' : 'an Excel dataset'} and Datasphere will
                  automatically validate, profile and prepare it for AI-powered analytics.
                </p>
              </div>
            </div>
          </>
        )}

        {phase === 'analyzing' && <AnalyzingProgress steps={ANALYZING_STEPS} done={analyzingDone} />}

        {phase === 'analysis' && (
          <UploadAnalysisScreen connection={analysis} onStartChat={handleStartChat} onBack={handleBackFromAnalysis} />
        )}

        {phase === 'invalid' && (
          <UploadValidationErrors issues={uploadValidation} onChooseDifferentFile={chooseDifferentFile} />
        )}

        {phase === 'form' && (
          <form onSubmit={handleSubmit} className="connect-page-grid">
            <div style={s.formCol}>
              <section style={s.sectionCard}>
                <div style={s.sectionHeader}>
                  <span style={s.stepTag}>Step 1</span>
                  <h3 style={s.sectionTitle}>Connection Information</h3>
                </div>
                <div style={s.field}>
                  <label style={s.label}>Connection Name</label>
                  <input
                    style={s.input}
                    placeholder="e.g. Sales Report"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    autoFocus
                  />
                  <span style={s.hint}>This is how the dataset will appear in your workspace.</span>
                </div>
              </section>

              <div style={s.connector} aria-hidden="true">
                <span style={s.connectorLine} />
              </div>

              <section style={s.sectionCard}>
                <div style={s.sectionHeader}>
                  <span style={s.stepTag}>Step 2</span>
                  <h3 style={s.sectionTitle}>Upload Dataset</h3>
                </div>
                <div
                  style={{ ...s.uploadCard, ...(dragOver ? s.uploadCardActive : {}), ...(file ? s.uploadCardHasFile : {}) }}
                  className="connect-upload-card"
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileRef.current.click()}
                  role="button"
                  tabIndex={0}
                >
                  <input
                    ref={fileRef}
                    type="file"
                    style={{ display: 'none' }}
                    accept={meta.accept}
                    onChange={e => e.target.files[0] && handleFile(e.target.files[0])}
                  />
                  {file ? (
                    <div style={s.uploadFileRow}>
                      <div style={s.uploadFileIcon}>
                        <FontAwesomeIcon icon={meta.icon} aria-hidden="true" />
                      </div>
                      <div style={s.uploadFileMeta}>
                        <strong style={s.uploadFileName}>{file.name}</strong>
                        <span style={s.uploadFileSize}>{(file.size / 1024).toFixed(1)} KB &middot; ready to upload</span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setFile(null); }}
                        style={s.uploadRemoveBtn}
                        aria-label="Remove file"
                      >
                        <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                      </button>
                    </div>
                  ) : (
                    <div style={s.uploadEmpty}>
                      <div style={{ ...s.uploadBadge, ...(dragOver ? s.uploadBadgeActive : {}) }}>
                        <FontAwesomeIcon icon={faArrowUpFromBracket} aria-hidden="true" />
                      </div>
                      <strong style={s.uploadTitle}>
                        {dragOver ? 'Drop it here' : 'Drag and drop your file here'}
                      </strong>
                      <span style={s.uploadText}>Supported formats: {meta.label === 'CSV' ? 'CSV' : 'XLS, XLSX'}</span>
                      <span style={s.uploadDivider}>or</span>
                      <button type="button" style={s.browseBtn} onClick={(e) => { e.stopPropagation(); fileRef.current.click(); }}>
                        Browse Files
                      </button>
                    </div>
                  )}
                </div>
              </section>

              <div style={s.actions}>
                <Link to="/dashboard" style={s.cancelBtn}>Cancel</Link>
                <button
                  type="submit"
                  disabled={submitDisabled}
                  style={{ ...s.submitBtn, ...(submitDisabled ? s.submitBtnDisabled : {}) }}
                >
                  {timeoutCooldown ? 'Still processing…' : isSubmitting ? 'Uploading…' : 'Upload & Analyze'}
                </button>
              </div>
            </div>

            <div className="connect-page-requirements">
              <UploadRequirementsPanel />
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'var(--bg-primary)', padding: '24px 24px 56px' },
  container: { maxWidth: 1040, marginInline: 'auto' },
  backLink: {
    display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600,
    color: 'var(--text-secondary)', textDecoration: 'none', marginBottom: 18,
  },
  header: { display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 26 },
  headerIcon: {
    width: 46, height: 46, borderRadius: 14, background: 'var(--accent-subtle)',
    border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'var(--accent)', fontSize: 18, flexShrink: 0,
  },
  title: { fontSize: 25, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em', marginBottom: 6 },
  subtitle: { fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: 620 },
  formCol: { display: 'flex', flexDirection: 'column', gap: 0, minWidth: 0 },
  connector: { display: 'flex', justifyContent: 'flex-start', paddingLeft: 24, height: 16 },
  connectorLine: { width: 2, height: '100%', background: 'var(--border-subtle)' },
  sectionCard: {
    border: '1px solid var(--border-subtle)', borderRadius: 16,
    background: 'var(--bg-secondary)', padding: 20, display: 'flex', flexDirection: 'column', gap: 14,
    boxShadow: 'var(--shadow-sm)',
  },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: 10 },
  stepTag: {
    fontSize: 10, fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase',
    color: 'var(--accent)', background: 'var(--accent-subtle)', padding: '4px 9px', borderRadius: 999,
  },
  sectionTitle: { fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.6px' },
  input: {
    height: 44, padding: '0 14px', borderRadius: 11, border: '1.5px solid var(--border-subtle)',
    background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 14, outline: 'none',
    fontFamily: 'var(--font-main)', transition: 'border-color 0.15s ease',
  },
  hint: { fontSize: 11.5, color: 'var(--text-muted)' },
  textarea: {
    padding: '10px 14px', borderRadius: 11, border: '1.5px solid var(--border-subtle)',
    background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 14, outline: 'none',
    fontFamily: 'var(--font-main)', resize: 'vertical',
  },
  uploadCard: {
    border: '1.5px dashed var(--border-subtle)', borderRadius: 16, padding: 22,
    background: 'var(--bg-primary)', cursor: 'pointer', minHeight: 176,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.18s ease',
  },
  uploadCardActive: { borderColor: 'var(--accent)', background: 'var(--accent-subtle)' },
  uploadCardHasFile: { borderColor: 'var(--success)', background: 'var(--success-subtle)', borderStyle: 'solid' },
  uploadEmpty: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' },
  uploadBadge: {
    width: 56, height: 56, borderRadius: 18, background: 'var(--bg-secondary)',
    border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'var(--accent)', fontSize: 20, marginBottom: 4, transition: 'all 0.18s ease',
  },
  uploadBadgeActive: { background: 'var(--accent)', color: '#fff', transform: 'scale(1.08)' },
  uploadTitle: { fontSize: 15, color: 'var(--text-primary)', fontWeight: 800 },
  uploadText: { fontSize: 13, color: 'var(--text-secondary)' },
  uploadDivider: { fontSize: 12, color: 'var(--text-muted)' },
  browseBtn: {
    padding: '10px 16px', borderRadius: 12, border: '1px solid var(--border-subtle)',
    background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  uploadFileRow: { display: 'flex', alignItems: 'center', gap: 12, width: '100%' },
  uploadFileIcon: {
    width: 44, height: 44, borderRadius: 14, background: 'var(--bg-secondary)',
    border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'var(--accent)', flexShrink: 0,
  },
  uploadFileMeta: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 },
  uploadFileName: { fontSize: 14, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  uploadFileSize: { fontSize: 12, color: 'var(--text-muted)' },
  uploadRemoveBtn: {
    width: 34, height: 34, borderRadius: 10, border: '1px solid var(--border-subtle)',
    background: 'var(--bg-secondary)', color: 'var(--text-muted)', cursor: 'pointer', flexShrink: 0,
  },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 },
  cancelBtn: {
    padding: '11px 18px', borderRadius: 12, border: '1.5px solid var(--border-subtle)',
    background: 'none', color: 'var(--text-secondary)', fontSize: 14, cursor: 'pointer',
    textDecoration: 'none', display: 'flex', alignItems: 'center',
  },
  submitBtn: {
    padding: '11px 22px', borderRadius: 12, background: 'var(--accent)', color: '#fff',
    fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 'none',
    boxShadow: '0 8px 20px rgba(37, 99, 235, 0.28)', transition: 'all 0.15s ease',
  },
  submitBtnDisabled: { opacity: 0.55, boxShadow: 'none', cursor: 'not-allowed' },
};
